import {useParams} from 'react-router-dom';
import React, {useRef, useEffect, useState} from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {usePrintActivitiesMutation} from '../../redux/activitiesApi';
import {
	OrderInPrintOrderType,
	ResponseOrderType,
} from '../../types/TypesDepotPaperwork';
import {useTranslation} from 'react-i18next';
import s from './PDFView.module.css';
import InfoErrorSuccessPopup from '../Notification/InfoMessegesPopups/InfoErrorSuccessPopup';
import GreenButton from '../Buttons/GreenButton';
import logo from '../../assets/img/clogo5.png';

const PaperworkPDFView = () => {
	const {id} = useParams();
	const pdfRef = useRef<HTMLDivElement | null>(null);
	const {t} = useTranslation();
	const [printActivities] = usePrintActivitiesMutation();
	const [order, setOrder] = useState<ResponseOrderType | null>(null);

	useEffect(() => {
		if (id) {
			printActivities(+id)
				.unwrap()
				.then((res: ResponseOrderType) => setOrder(res));
		}
	}, [id, printActivities]);

	const generatePDF = () => {
		if (!pdfRef.current) return;
		html2canvas(pdfRef.current, {scale: 2, useCORS: true}).then((canvas) => {
			const imgData = canvas.toDataURL('image/png');
			const pdf = new jsPDF('p', 'mm', 'a4');
			const pageWidth = pdf.internal.pageSize.getWidth();
			const imgHeight = (canvas.height * pageWidth) / canvas.width;

			pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, imgHeight);
			window.open(pdf.output('bloburl'));
		});
	};

	if (!order)
		return (
			<div>
				<InfoErrorSuccessPopup type={'noStructure'} text={'loading...'} />
			</div>
		);
	return (
		<main className="page">
			{order?.order.map((order: OrderInPrintOrderType) => (
				<div
					key={order.id}
					className="page__container container"
					ref={pdfRef}
					style={{
						padding: 20,
						fontSize: '14px',
						background: '#fff',
						width: '210mm',
						minHeight: '297mm',
						fontFamily: 'Arial, sans-serif',
					}}
				>
					<div>
						{/* Header with logo */}
						<div className={s.headerBlock}>
							<div className={s.logoContainer}>
								<img src={logo} alt="Choice Logo" className={s.logo} />
							</div>
							<div className={s.companyInfo}>
								<h2>Choice</h2>
								<p>Система управління замовленнями</p>
								<p>cabinet.choice.ua</p>
							</div>
						</div>

						{/* Title */}
						<div className={s.titleBlock}>
							<h1>
								{t('Оформлення')} №{order.id}
							</h1>
						</div>

						{/* Даты и имена */}
						<div className={s.topBlock}>
							<div className={s.infoSection}>
								<h3>{t('Інформація про замовлення')}</h3>
								<p>
									<strong>{t('Дата проведення')}:</strong> {order.order_date}
								</p>
								<p>
									<strong>{t('Дата створення')}:</strong> {order.create_date}
								</p>
							</div>
							<div className={s.infoSection}>
								<h3>{t('Учасники')}</h3>
								<p>
									<strong>{t("Ім'я покупця")}:</strong> {order.user_name}
								</p>
								<p>
									<strong>{t("Ім'я продавця")}:</strong> {order.ware_name}
								</p>
							</div>
						</div>

						{/* Таблица продуктов */}
						<div className={s.productsSection}>
							<h3>{t('Товари та послуги')}</h3>
							<table className={s.table}>
								<thead>
									<tr>
										<th>{t('Назва товару')}</th>
										<th>{t('Кількість')}</th>
										<th>{t('Ціна за од.')}</th>
										<th>{t('Бали за од.')}</th>
										<th>{t('Сума балів')}</th>
										<th>
											{t('Сума грошей')} {order.currency}
										</th>
									</tr>
								</thead>
								<tbody>
									{order.products?.map((p, idx) => (
										<tr key={idx}>
											<td>{p.name}</td>
											<td>{p.count}</td>
											<td>
												{p.money} {order.currency}
											</td>
											<td>{p.points} Б</td>
											<td>{p.sum_points} Б</td>
											<td>
												{p.sum_money} {order.currency}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{/* Итоги */}
						<div className={s.bottomInfo}>
							<div className={s.summarySection}>
								<h3>{t('Підсумки')}</h3>
								<div className={s.summaryRow}>
									<span>{t('Всього балів')}:</span>
									<span>{order.sum_points} Б</span>
								</div>
								<div className={s.summaryRow}>
									<span>{t('Всього грошей')}:</span>
									<span>
										{order.sum_money} {order.currency}
									</span>
								</div>
								{order.discount > 0 && (
									<div className={s.summaryRow}>
										<span>{t('Розмір знижки')}:</span>
										<span>
											{order.discount} {order.currency}
										</span>
									</div>
								)}
								{order.bonus > 0 && (
									<div className={s.summaryRow}>
										<span>{t('Розмір знятого бонусу')}:</span>
										<span>{order.bonus} Б</span>
									</div>
								)}
								<div className={`${s.summaryRow} ${s.totalRow}`}>
									<span>
										<strong>{t('До сплати')}:</strong>
									</span>
									<span>
										<strong>
											{order.pay_money} {order.currency}
										</strong>
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			))}
			<div className={s.buttonContainer}>
				<GreenButton text={t('Згенерувати PDF')} onClick={generatePDF} />
			</div>
		</main>
	);
};

export default PaperworkPDFView;
